import { Dispatch, FC, SetStateAction, useMemo, useState } from 'react';
import { IndividualStudentReportStatsQuery } from '../../graphQLOperations/__generated__/IndividualStudentReportStatsQuery.graphql';
import { PreloadedQuery, usePreloadedQuery } from 'react-relay';
import { getStudentReportStatsAndSkillsWeekly } from '../../graphQLOperations/IndividualStudentReport';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { IndividualReportNoDailyActivity } from './IndividualReportNoDailyActivity';
import { formatDateWithTimezone } from '../../businessLogicService/dateFormatting';
import { SelectedSkillIndividualReport } from './IndividualReportWrapper';
import IndividualReportDailySkill from './IndividualReportDailySkill';
import IndividualReportDailyExitSlip from './IndividualReportDailyExitSlip';
import IndividualReportDailyExitSlipModalWrapper from './IndividualReportDailyExitSlipModalWrapper';

interface Props {
  weeklyStatsRef: PreloadedQuery<IndividualStudentReportStatsQuery, Record<string, unknown>>;
  setSelectedSkill: Dispatch<SetStateAction<SelectedSkillIndividualReport | null>>;
  setSelectedModalSkill: Dispatch<SetStateAction<{ code: string } | null>>;
}

export interface ExitSlipModalData {
  sessionId: string | null;
  activityId: string | null;
  publicUserId: string | null;
  title: string;
}

interface DailyActivityGroup {
  day: string;
  skills: IndividualStudentReportStatsQuery['response']['individualReportDailyActivity'][number]['dailyActivitySkills'];
  exitSlips: IndividualStudentReportStatsQuery['response']['classExitSlips'];
}

export const IndividualReportDailyActivity: FC<Props> = ({
  weeklyStatsRef,
  setSelectedSkill,
  setSelectedModalSkill,
}) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [modalData, setModalData] = useState<ExitSlipModalData>({
    sessionId: null,
    activityId: null,
    publicUserId: null,
    title: '',
  });

  const { individualReportDailyActivity, classExitSlips } = usePreloadedQuery(
    getStudentReportStatsAndSkillsWeekly,
    weeklyStatsRef,
  );

  const groupedDailyData = useMemo(() => {
    const map = new Map<string, DailyActivityGroup>();

    // skills
    individualReportDailyActivity.forEach(activity => {
      const key = formatDateWithTimezone(activity.day, 'UTC', 'yyyy-MM-dd');

      map.set(key, {
        day: activity.day,
        skills: activity.dailyActivitySkills,
        exitSlips: [],
      });
    });

    // exit slips
    classExitSlips.forEach(exitSlip => {
      const key = formatDateWithTimezone(exitSlip.dueOn, 'UTC', 'yyyy-MM-dd');

      const existing = map.get(key);

      if (existing) {
        existing.exitSlips = [...existing.exitSlips, exitSlip];
      } else {
        map.set(key, {
          day: exitSlip.dueOn,
          skills: [],
          exitSlips: [exitSlip],
        });
      }
    });

    return Array.from(map.values())
      .filter(dayGroup => dayGroup.skills.length > 0 || dayGroup.exitSlips.length > 0)
      .sort((a, b) => new Date(b.day).getTime() - new Date(a.day).getTime());
  }, [individualReportDailyActivity, classExitSlips]);

  return (
    <>
      {groupedDailyData.length ? (
        groupedDailyData.map(dayGroup => (
          <Accordion key={dayGroup.day} multiple activeIndex={[0]} className="tw-mt-8" id="dailyActivityReport">
            <AccordionTab
              className="tw-border-t-0 tw-border-l-0 tw-border-r-0 tw-border-[2px] tw-border-solid tw-border-neutral-300"
              style={{ borderRadius: 0, boxShadow: 'none' }}
              headerClassName="tw-border-none tw-border-0 tw-shadow-none"
              disabled={!dayGroup.day}
              header={
                <p className="tw-font-semibold tw-text-lg tw-ml-2">
                  {formatDateWithTimezone(dayGroup.day, 'UTC', "cccc',' dd MMMM")}
                </p>
              }
            >
              {/* Exit slips */}
              {dayGroup.exitSlips.map(exitSlip => (
                <IndividualReportDailyExitSlip
                  key={exitSlip.id}
                  exitSlip={exitSlip}
                  setIsVisible={setIsVisible}
                  setModalData={setModalData}
                />
              ))}

              {/* Skills */}
              {dayGroup.skills.map(skill => (
                <IndividualReportDailySkill
                  key={skill.code}
                  skill={skill}
                  setSelectedSkill={setSelectedSkill}
                  setSelectedModalSkill={setSelectedModalSkill}
                />
              ))}
            </AccordionTab>
          </Accordion>
        ))
      ) : (
        <div className="tw-mt-5">
          <IndividualReportNoDailyActivity />
        </div>
      )}
      {isVisible && (
        <IndividualReportDailyExitSlipModalWrapper
          modalData={modalData}
          isVisible={isVisible}
          setIsVisible={setIsVisible}
        />
      )}
    </>
  );
};
